import React, { useState, useEffect, useRef } from 'react';
import API_URL from '../config';
import axios from 'axios';
import { showTextDelayNotice } from './shared/sendNotice';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import SharedNav from './SharedNav';
import FaProofModal from './FaProofModal';
import EditCandidateModal from './shared/EditCandidateModal';
import { RoleBadge, useJobRoles } from './shared/RoleField';
import RecruiterField, { reassignIfChanged } from './shared/RecruiterField';
import canReassign from './shared/canReassign';
import TextCandidateModal from './shared/TextCandidateModal';
import { canOverride, canContactCandidate } from './shared/overrideUsers';
import TimelineDrawer from './shared/TimelineDrawer';
import ClockButton from './shared/ClockButton';
import Avatar from './shared/Avatar';
import StatusLabel from './shared/StatusLabel';
import RatingRing from './shared/RatingRing';
import { callNumber } from './shared/rcWidget';
import './CandidateArchive.css';

// Same picker options as AccountsPage.js's company sentiment -- reusing its exact CSS
// classes (acc-sent-*) so the colors/shapes/borders match without redefining anything.
const SENTIMENT_COLORS = ['green', 'mustard', 'red'];

function experienceTag(candidate) {
  try {
    const profile = typeof candidate.candidate_profile === 'string'
      ? JSON.parse(candidate.candidate_profile)
      : candidate.candidate_profile;
    const trades = profile?.trade_types;
    if (trades && trades.length > 0) return trades.slice(0, 3).join(' · ');
  } catch {}
  return candidate.last_worked || '—';
}

// "Not Responding" / "Not Interested" are the two long screening_status values -- abbreviated
// so every pill in the Screened column renders at the same width regardless of which status a
// candidate has. Everything else (Pass/Fail/blank) is already short and passes through as-is.
function screenedLabel(status) {
  if (status === 'Not Responding') return 'No R';
  if (status === 'Not Interested') return 'No I';
  return undefined;
}

function CandidateArchive({ user, onLogout }) {
  const navigate = useNavigate();
  const [textCandidate, setTextCandidate] = useState(null);
  const [timelineId, setTimelineId] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [csas, setCsas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [remarksEdits, setRemarksEdits] = useState({});
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [faProofCandidate, setFaProofCandidate] = useState(null);
  const [formData, setFormData] = useState({});
  const [runningArchive, setRunningArchive] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sentToClient, setSentToClient] = useState('all');
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [showRatingDropdown, setShowRatingDropdown] = useState(false);
  const [selectedSentiment, setSelectedSentiment] = useState('');
  const [selectedSourceType, setSelectedSourceType] = useState('');
  const [selectedJobRole, setSelectedJobRole] = useState('');
  const { roles: jobRoles } = useJobRoles();
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sentimentOpen, setSentimentOpen] = useState(null);
  const ratingDropdownRef = useRef(null);
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  // Close sentiment picker on outside click -- same pattern as AccountsPage.js
  useEffect(() => {
    if (sentimentOpen === null) return;
    const close = () => setSentimentOpen(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [sentimentOpen]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const toggleRating = (rating) => {
    setSelectedRatings(prev =>
      prev.includes(rating) ? prev.filter(r => r !== rating) : [...prev, rating]
    );
  };

  const clearAllFilters = () => {
    setSelectedCompany('');
    setDateFilter('all');
    setStartDate('');
    setEndDate('');
    setSentToClient('all');
    setSelectedRatings([]);
    setSelectedSentiment('');
    setSelectedSourceType('');
    setSelectedJobRole('');
  };

  const hasActiveFilters = selectedCompany || dateFilter !== 'all' || sentToClient !== 'all' || selectedRatings.length > 0 || selectedSentiment || selectedSourceType || selectedJobRole;

  const toTitleCase = (str) => {
    if (!str) return str;
    return str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const calculateDays = (screeningDate) => {
    if (!screeningDate) return '-';
    const screening = new Date(screeningDate);
    const today = new Date();
    const diffTime = Math.abs(today - screening);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const getNYDate = () => moment().tz('America/New_York').format('YYYY-MM-DD');

  const defaultForm = {
    company_id: '',
    candidate_name: '',
    fedex_id: '',
    phone: '',
    email: '',
    location: '',
    last_worked: '',
    screening_date: getNYDate(),
    bg_status: '',
    drug_status: '',
    physical_status: '',
    gcic_status: '',
    remarks: '',
    screening_status: '',
    fadv_status: '',
    fadv_link: '',
    indeed_url: '',
    is_high_priority: false,
    job_role: '',
  };

  useEffect(() => {
    setCurrentPage(0);
    fetchArchive(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, dateFilter, startDate, endDate, sentToClient, selectedRatings, selectedSentiment, selectedSourceType, selectedJobRole]);

  // Debounced server-side search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(0);
      fetchArchive(0, false);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchCsas();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ratingDropdownRef.current && !ratingDropdownRef.current.contains(e.target)) {
        setShowRatingDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchArchive = async (page = 0, append = false) => {
    if (append) setIsLoadingMore(true);
    else setLoading(true);
    try {
      const params = {};
      if (selectedCompany) params.companyId = selectedCompany;
      if (dateFilter && dateFilter !== 'all') {
        params.dateFilter = dateFilter;
        if (dateFilter === 'custom' && startDate && endDate) {
          params.startDate = startDate;
          params.endDate = endDate;
        }
      }
      if (sentToClient !== 'all') params.sentToClient = sentToClient;
      if (selectedRatings.length > 0) params.ratings = selectedRatings.join(',');
      if (selectedSentiment) params.sentiment = selectedSentiment;
      if (selectedSourceType) params.sourceType = selectedSourceType;
      if (selectedJobRole) params.jobRole = selectedJobRole;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (!searchTerm.trim() && page > 0) params.page = page;

      const res = await axios.get(`${API_URL}/api/candidates/archive`, { headers, params });
      const data = res.data;
      const newCandidates = data.candidates || data; // backwards compat
      const newTotal = data.total ?? newCandidates.length;
      if (append) {
        setCandidates(prev => [...prev, ...newCandidates]);
      } else {
        setCandidates(newCandidates);
      }
      setTotalCount(newTotal);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching archive:', err);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/companies`, { headers });
      setCompanies(res.data || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchCsas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/csas`, { headers });
      setCsas(res.data || []);
    } catch (err) {
      console.error('Error fetching CSAs:', err);
    }
  };

  const saveRemarks = async (id, value) => {
    try {
      await axios.patch(`${API_URL}/api/candidates/${id}/remarks`, { remarks: value }, { headers });
      setCandidates(prev => prev.map(c => c.id === id ? { ...c, remarks: value } : c));
    } catch (err) {
      console.error('Error saving remarks:', err);
    }
  };

  const saveSentiment = async (id, value) => {
    try {
      await axios.patch(`${API_URL}/api/candidates/${id}/sentiment`, { sentiment: value }, { headers });
      setCandidates(prev => prev.map(c => c.id === id ? { ...c, sentiment: value } : c));
    } catch (err) {
      console.error('Error saving sentiment:', err);
    }
    setSentimentOpen(null);
  };

  const togglePriority = async (candidate) => {
    try {
      await axios.post(`${API_URL}/api/candidates/${candidate.id}/toggle-priority`, { is_high_priority: !candidate.is_high_priority }, { headers });
      setCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, is_high_priority: !candidate.is_high_priority } : c));
    } catch (err) {
      console.error('Error toggling priority:', err);
    }
  };

  const handleEdit = (candidate) => {
    setEditingCandidate(candidate);
    setFormData({
      recruiter_id: candidate.recruiter_id,
      company_id: candidate.company_id || '',
      csa_id: candidate.csa_id || '',
      candidate_name: candidate.candidate_name || '',
      fedex_id: candidate.fedex_id || '',
      phone: candidate.phone || '',
      email: candidate.email || '',
      location: candidate.location || '',
      last_worked: candidate.last_worked || '',
      screening_date: candidate.screening_date || getNYDate(),
      bg_status: candidate.bg_status || '',
      drug_status: candidate.drug_status || '',
      drug_test_pass_date: candidate.drug_test_pass_date ? candidate.drug_test_pass_date.split('T')[0] : '',
      physical_status: candidate.physical_status || '',
      gcic_status: candidate.gcic_status || '',
      remarks: candidate.remarks || '',
      screening_status: candidate.screening_status || '',
      fadv_status: candidate.fadv_status || '',
      indeed_url: candidate.indeed_url || '',
      is_high_priority: candidate.is_high_priority || false,
      mountain_rating: candidate.mountain_rating || '',
      experience_summary: candidate.experience_summary || '',
      blacklisted: !!candidate.blacklisted,
      blacklist_reason: candidate.blacklist_reason || '',
      _wasBlacklisted: !!candidate.blacklisted,
      fedex_history: candidate.fedex_history || '',
      fadv_file_transfer: candidate.fadv_file_transfer || '',
      job_role: candidate.job_role || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/candidates/${editingCandidate.id}`, formData, { headers });
        if (canReassign(user)) await reassignIfChanged(editingCandidate, formData.recruiter_id);
      await fetchArchive();
      setShowForm(false);
      setEditingCandidate(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Error updating candidate');
    }
  };

  // Same pre-checks as RecruiterPortal.js's Driver Candidates table -- kept in sync since both
  // pages hit the same backend endpoint, which enforces all of these regardless (this is just
  // the fast client-side path so a recruiter doesn't have to wait on a round trip to find out).
  const sendToClient = (candidate) => {
    // Drug/physical is no longer checked here from CRM fields (removed October 2026): the FA
    // screenshot check on the server covers it, with Send anyway for the override list.
    if (!candidate.job_role && (!candidate.mountain_rating || candidate.mountain_rating < 5)) {
      alert('This candidate is rated below 5 and cannot be sent to the client. Update their rating to 5 or above first.');
      return;
    }
    if (!candidate.csa_id) {
      alert('This candidate has no CSA set and cannot be sent to the client. Update their CSA first.');
      return;
    }
    if (!candidate.fedex_id || !String(candidate.fedex_id).trim()) {
      alert('Please fill out the FedEx ID to send to client.');
      return;
    }
    // Ex/Current FedEx with "Not Transferred": no driver file exists to screenshot, so send
    // straight away without the FA proof modal (the server allows this case only).
    if (candidate.fedex_history && candidate.fadv_file_transfer === 'Not Transferred') {
      if (!window.confirm(`Send ${candidate.candidate_name} to the client? Their FADV file was not transferred, so no FA screenshot is needed.`)) return;
      sendWithoutFaProof(candidate);
      return;
    }
    setFaProofCandidate(candidate);
  };

  const sendWithoutFaProof = async (candidate) => {
    try {
      const r = await axios.post(`${API_URL}/api/candidates/${candidate.id}/send-to-client`, new FormData(), { headers });
      handleFaProofSuccess(r.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Error sending candidate to client.');
    }
  };

  const handleFaProofSuccess = async (data) => {
    setFaProofCandidate(null);
    showTextDelayNotice(data);
    await fetchArchive();
  };

  const resetInterviewStatus = async (candidateId) => {
    if (!window.confirm('Remove this candidate from Ready for Interview?')) return;
    try {
      await axios.post(`${API_URL}/api/candidates/${candidateId}/reset-interview`, {}, { headers });
      alert('Candidate removed from interview list');
      fetchArchive();
    } catch (err) {
      console.error('Error resetting interview status:', err);
      alert(err.response?.data?.error || 'Error resetting interview status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this candidate?')) return;
    try {
      await axios.delete(`${API_URL}/api/candidates/${id}`, { headers });
      fetchArchive();
    } catch (err) {
      console.error('Error deleting candidate:', err);
      alert('Error deleting candidate');
    }
  };

  const getRatingColor = (rating) => {
    if (!rating) return '';
    if (rating >= 8) return '#2e7d32';
    if (rating >= 5) return '#f57c00';
    return '#c62828';
  };

  const canLoadMore = !searchTerm.trim() && candidates.length < totalCount;

  return (
    <div className="candidate-archive">
      <SharedNav user={user} onLogout={onLogout} />

      <div className="portal-content">
        <div className="portal-header">
          <div className="header-left-section">
            <h2>Archived Candidates ({searchTerm.trim() ? `${candidates.length} result${candidates.length !== 1 ? 's' : ''}` : `Showing ${candidates.length} of ${totalCount}`})</h2>
            <p className="archive-subtitle">Candidates screened more than 60 days ago are automatically moved here.</p>
          </div>
          {isAdmin && (
            <button
              className="run-archive-btn"
              disabled={runningArchive}
              onClick={async () => {
                setRunningArchive(true);
                try {
                  const res = await axios.post(`${API_URL}/api/candidates/archive/run`, {}, { headers });
                  alert(res.data.message);
                  fetchArchive();
                } catch (e) {
                  alert('Archive run failed: ' + (e.response?.data?.error || e.message));
                }
                setRunningArchive(false);
              }}
            >
              {runningArchive ? 'Running…' : 'Run Archive Now'}
            </button>
          )}
        </div>

        <div className="filter-bar">
          <input
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="filter-select">
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <select value={dateFilter} onChange={(e) => { setDateFilter(e.target.value); if (e.target.value !== 'custom') { setStartDate(''); setEndDate(''); } }} className="filter-select">
            <option value="all">All Dates</option>
            <option value="thisWeek">This Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="last3Months">Last 3 Months</option>
            <option value="custom">Custom Range</option>
          </select>
          {dateFilter === 'custom' && (
            <>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="filter-date-input" />
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="filter-date-input" />
            </>
          )}

          <select value={sentToClient} onChange={(e) => setSentToClient(e.target.value)} className="filter-select">
            <option value="all">All Status</option>
            <option value="yes">Sent to Client</option>
            <option value="no">Not Sent</option>
          </select>

          <div className="rating-filter-container" ref={ratingDropdownRef}>
            <button
              className={`rating-filter-btn ${selectedRatings.length > 0 ? 'active' : ''}`}
              onClick={() => setShowRatingDropdown(!showRatingDropdown)}
            >
              {selectedRatings.length > 0 ? `Rating: ${selectedRatings.sort((a, b) => a - b).join(', ')}` : 'All Ratings'} ▾
            </button>
            {showRatingDropdown && (
              <div className="rating-dropdown">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(r => (
                  <label key={r} className="rating-option">
                    <input
                      type="checkbox"
                      checked={selectedRatings.includes(r)}
                      onChange={() => toggleRating(r)}
                    />
                    <span style={{ color: getRatingColor(r), fontWeight: 600 }}>{r}</span>
                  </label>
                ))}
                {selectedRatings.length > 0 && (
                  <button className="clear-ratings-btn" onClick={() => setSelectedRatings([])}>Clear</button>
                )}
              </div>
            )}
          </div>

          {/* Sentiment filter */}
          <select value={selectedSentiment} onChange={(e) => setSelectedSentiment(e.target.value)} className="filter-select">
            <option value="">All Sentiments</option>
            <option value="green">Green</option>
            <option value="mustard">Orange</option>
            <option value="red">Red</option>
            <option value="none">No Sentiment</option>
          </select>

          {/* Prior vs New filter */}
          <select value={selectedSourceType} onChange={(e) => setSelectedSourceType(e.target.value)} className="filter-select">
            <option value="">Prior + New</option>
            <option value="prior">Prior only</option>
            <option value="new">New only</option>
          </select>

          {/* Role filter (October 2026) */}
          <select value={selectedJobRole} onChange={(e) => setSelectedJobRole(e.target.value)} className="filter-select">
            <option value="">All roles</option>
            <option value="driver">Drivers</option>
            {jobRoles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
          </select>

          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearAllFilters}>✕ Clear Filters</button>
          )}
        </div>

        {loading ? (
          <div className="loading-state">Loading archived candidates...</div>
        ) : (
          <div className="candidates-table-container mtn-table-shell">
          <div className="mtn-scroll-x">
            <table className="candidates-table" style={{ minWidth: '1400px' }}>
              <thead>
                <tr>
                  <th>Days</th>
                  <th>Candidates</th>
                  <th>Owner</th>
                  <th>Sentiment</th>
                  <th>Remarks</th>
                  <th>CSA</th>
                  <th>Screened</th>
                  <th>Score</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Actions</th>
                  <th>Resume</th>
                  <th>FADV</th>
                  <th>Location</th>
                  <th>Experience</th>
                  <th>Priority</th>
                  <th>BG</th>
                  <th>Drug</th>
                  <th>Drug Test Date</th>
                  <th>Physical</th>
                  <th>GCIC</th>
                  <th>Screening Date</th>
                  <th>Archived</th>
                  <th>FedEx ID</th>
                </tr>
              </thead>
              <tbody>
                {candidates.length === 0 ? (
                  <tr>
                    <td colSpan="24" className="empty-state">
                      No archived candidates found.
                    </td>
                  </tr>
                ) : (
                  candidates.map(candidate => (
                    <tr key={candidate.id}>
                      <td className="days-cell">
                        <span className="days-badge">{calculateDays(candidate.screening_date)}</span>
                      </td>
                      <td className="candidate-name-cell">
                        <span className="mtn-name-row">
                          <Avatar name={candidate.candidate_name} size="sm" />
                          {(candidate.blacklisted || candidate.phone_blacklist_reason) && (
                            <span className="fadv-flag-wrapper">
                              <span className="blacklist-flag">🏴</span>
                              <span className="fadv-flag-tooltip">
                                Blacklisted{candidate.blacklisted ? '' : ' (same phone as a blacklisted record)'}: {candidate.blacklisted ? candidate.blacklist_reason : candidate.phone_blacklist_reason}
                              </span>
                            </span>
                          )}
                          {candidate.mountain_resume_url ? (
                            <a href={`/${candidate.mountain_resume_url}`} target="_blank" rel="noopener noreferrer" className="candidate-name-link candidate-name-truncate" title={candidate.candidate_name}>
                              {toTitleCase(candidate.candidate_name)}
                            </a>
                          ) : (
                            <span className="candidate-name-truncate" title={candidate.candidate_name}>{toTitleCase(candidate.candidate_name)}</span>
                          )}
                          <RoleBadge role={candidate.job_role} />
                          <ClockButton onClick={() => setTimelineId(candidate.id)} />
                        </span>
                      </td>
                      <td className="recruiter-cell" title={candidate.recruiter_name || ''}>
                        {candidate.recruiter_name ? candidate.recruiter_name.trim().split(/\s+/)[0] : '—'}
                      </td>
                      <td className="acc-sentiment-td">
                        <div className="acc-sentiment-cell" onClick={e => e.stopPropagation()}>
                          <button
                            className={`acc-sent-dot acc-sent-${candidate.sentiment || 'none'}`}
                            onClick={() => setSentimentOpen(sentimentOpen === candidate.id ? null : candidate.id)}
                            title="Set sentiment"
                          />
                          {sentimentOpen === candidate.id && (
                            <div className="acc-sent-picker">
                              {SENTIMENT_COLORS.map(c => (
                                <button
                                  key={c}
                                  className={`acc-sent-dot acc-sent-${c}`}
                                  onClick={() => saveSentiment(candidate.id, c)}
                                />
                              ))}
                              <button
                                className="acc-sent-clear"
                                onClick={() => saveSentiment(candidate.id, null)}
                              >✕</button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="remarks-cell">
                        <textarea
                          className="remarks-inline"
                          value={remarksEdits[candidate.id] !== undefined ? remarksEdits[candidate.id] : (candidate.remarks || '')}
                          onChange={e => setRemarksEdits(prev => ({ ...prev, [candidate.id]: e.target.value }))}
                          onBlur={e => {
                            const val = e.target.value;
                            if (val !== (candidate.remarks || '')) saveRemarks(candidate.id, val);
                          }}
                          rows={1}
                          placeholder="Add note..."
                        />
                      </td>
                      <td className="csa-cell" title={candidate.csa_name || ''}>
                        {candidate.csa_name ? (
                          <span className="csa-badge">{candidate.csa_name}</span>
                        ) : '—'}
                      </td>
                      <td className="status-cell"><StatusLabel value={candidate.screening_status} label={screenedLabel(candidate.screening_status)} /></td>
                      <td className="rating-cell"><RatingRing rating={candidate.mountain_rating || null} /></td>
                      <td className="phone-cell">
                        {candidate.phone && canContactCandidate(user, candidate) && (
                          <button
                            type="button"
                            className="call-btn"
                            title={`Call ${candidate.candidate_name}`}
                            onClick={() => {
                              callNumber(candidate.phone);
                              axios.post(`${API_URL}/api/candidates/${candidate.id}/call-started`, {}, { headers }).catch(() => {});
                            }}
                          >
                            <svg viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.7 5.1 6.5 6.5l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1v3.4c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1L6.6 10.8z" /></svg>
                          </button>
                          )}
                          {candidate.phone && canContactCandidate(user, candidate) && (
                          <button
                            type="button"
                            className="call-btn text-btn"
                            title={`Text ${candidate.candidate_name}`}
                            onClick={() => setTextCandidate(candidate)}
                          >
                            <svg viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H8l-4 4V6c0-1.1.9-2 2-2z" /></svg>
                          </button>
                        )}
                        {candidate.phone}
                      </td>
                      <td
                        className="email-cell"
                        style={candidate.email?.includes('indeedemail.com') ? { color: 'red', fontWeight: 'bold' } : {}}
                        title={candidate.email}
                      >
                        {candidate.email}
                      </td>
                      <td className="actions-cell">
                        <button onClick={() => handleEdit(candidate)} className="mtn-btn mtn-btn-edit"><span className="mtn-ic">✏</span>Edit</button>
                        {candidate.interview_status === 'ready_for_interview' ? (
                          <button onClick={() => resetInterviewStatus(candidate.id)} className="mtn-btn mtn-btn-neutral mtn-btn-action-fixed">
                            <span className="mtn-ic">↑</span>Retract
                          </button>
                        ) : candidate.interview_status && candidate.interview_status !== 'pending' ? (
                          <button className="mtn-btn mtn-btn-neutral mtn-btn-disabled mtn-btn-action-fixed" disabled title="Client has already taken action">
                            Sent (Locked)
                          </button>
                        ) : (
                          <button onClick={() => sendToClient(candidate)} className="mtn-btn mtn-btn-send mtn-btn-action-fixed"><span className="mtn-ic">→</span>Send to Client</button>
                        )}
                        <button onClick={() => handleDelete(candidate.id)} className="mtn-btn mtn-btn-delete mtn-btn-icon-only" title="Delete">🗑</button>
                      </td>
                      <td className="resume-cell">
                        {candidate.indeed_url ? (
                          <a href={candidate.indeed_url} target="_blank" rel="noopener noreferrer" className="mtn-btn mtn-btn-primary">Resume</a>
                        ) : (
                          <button className="mtn-btn mtn-btn-primary mtn-btn-disabled" disabled>Resume</button>
                        )}
                      </td>
                      <td className="status-cell"><StatusLabel value={candidate.fadv_status} /></td>
                      <td className="location-cell">{candidate.location}</td>
                      <td className="experience-cell">{experienceTag(candidate)}</td>
                      <td className="priority-cell">
                        <button
                          onClick={() => togglePriority(candidate)}
                          className={`priority-toggle-btn ${candidate.is_high_priority ? 'active' : ''}`}
                          title={candidate.is_high_priority ? 'Remove from High Priority' : 'Mark as High Priority'}
                        >
                          {candidate.is_high_priority ? '⭐' : '☆'}
                        </button>
                      </td>
                      <td className="status-cell"><StatusLabel value={candidate.bg_status} /></td>
                      <td className="status-cell"><StatusLabel value={candidate.drug_status} /></td>
                      <td className="date-cell">{candidate.drug_test_pass_date ? new Date(candidate.drug_test_pass_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                      <td className="status-cell"><StatusLabel value={candidate.physical_status} /></td>
                      <td className="status-cell"><StatusLabel value={candidate.gcic_status} /></td>
                      <td className="date-cell">{candidate.screening_date}</td>
                      <td className="date-cell">
                        {candidate.archived_at
                          ? new Date(candidate.archived_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="fedex-id-cell">{candidate.fedex_id || ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {!loading && canLoadMore && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <button
              className="load-more-btn"
              onClick={() => fetchArchive(currentPage + 1, true)}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Loading…' : `Load More (${totalCount - candidates.length} remaining)`}
            </button>
          </div>
        )}
      </div>

      {showForm && editingCandidate && (
        <EditCandidateModal
          extraFields={editingCandidate && canReassign(user) ? (
            <RecruiterField value={formData.recruiter_id} currentId={editingCandidate.recruiter_id} currentName={editingCandidate.recruiter_name}
              onChange={v => setFormData(f => ({ ...f, recruiter_id: v }))} />
          ) : null}
          mode="edit"
          formData={formData}
          setFormData={setFormData}
          companies={companies}
          csas={csas}
          onSubmit={handleSubmit}
          onCancel={() => { setShowForm(false); setEditingCandidate(null); }}
          canRemoveBlacklist={canOverride(user)}
        />
      )}

      {timelineId && <TimelineDrawer candidateId={timelineId} user={user} onClose={() => setTimelineId(null)} />}

      {textCandidate && (
        <TextCandidateModal candidate={textCandidate} onClose={() => setTextCandidate(null)} />
      )}

      {faProofCandidate && (
        <FaProofModal
          candidate={faProofCandidate}
          onClose={() => setFaProofCandidate(null)}
          onSuccess={handleFaProofSuccess}
        />
      )}
    </div>
  );
}

export default CandidateArchive;
